import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MapPin,
  Plus,
  Loader2,
  RefreshCw,
  AlertCircle,
  Search,
  Edit,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from 'lucide-react';
import { getEnvironment } from '../../lib/cognito-config';

// Sort types
type SortDirection = 'asc' | 'desc' | null;
interface SortConfig {
  key: string;
  direction: SortDirection;
}

// Types
interface ZipMaster {
  zip: string;
  city: string;
  state: string;
  county: string;
  lat: number;
  lon: number;
  timezone: string;
  createdAt: string;
  buzzSeededAt: string | null;
  courtsSeededAt: string | null;
}

interface PageResponse {
  content: ZipMaster[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function ZipMasterListPage() {
  const environment = getEnvironment();

  // State
  const [zipCodes, setZipCodes] = useState<ZipMaster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Edit modal
  const [editingZip, setEditingZip] = useState<ZipMaster | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Load ZIP codes
  const loadZipCodes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let url = `/api/v1/admin/reference/zip-master/list?page=${page}&size=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const data = await fetchApi<PageResponse>(url);
      setZipCodes(data.content);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalElements);
    } catch (err) {
      console.error('Failed to load ZIP codes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ZIP codes');
      // Use mock data for development
      setZipCodes(getMockZipCodes());
      setTotalPages(3);
      setTotalItems(50);
    } finally {
      setIsLoading(false);
    }
  }, [search, page, pageSize]);

  // Save ZIP code
  const saveZipCode = async () => {
    if (!editingZip) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await fetchApi(`/api/v1/admin/reference/zip-master/${editingZip.zip}`, {
        method: 'PUT',
        body: JSON.stringify(editingZip),
      });
      setEditingZip(null);
      loadZipCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ZIP code');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete ZIP code
  const deleteZipCode = async (zip: string) => {
    if (!confirm(`Are you sure you want to delete ZIP code ${zip}?`)) return;

    try {
      await fetchApi(`/api/v1/admin/reference/zip-master/${zip}`, {
        method: 'DELETE',
      });
      loadZipCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete ZIP code');
    }
  };

  // Create new ZIP code
  const createZipCode = async () => {
    if (!editingZip) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await fetchApi(`/api/v1/admin/reference/zip-master`, {
        method: 'POST',
        body: JSON.stringify(editingZip),
      });
      setEditingZip(null);
      loadZipCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ZIP code');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load on mount and filter change
  useEffect(() => {
    loadZipCodes();
  }, [loadZipCodes]);

  // Mock data for development
  function getMockZipCodes(): ZipMaster[] {
    return [
      {
        zip: '10001',
        city: 'New York',
        state: 'NY',
        county: 'New York',
        lat: 40.7484,
        lon: -73.9967,
        timezone: 'America/New_York',
        createdAt: '2024-01-15T10:30:00Z',
        buzzSeededAt: '2024-01-16T08:00:00Z',
        courtsSeededAt: '2024-01-17T09:00:00Z',
      },
      {
        zip: '90210',
        city: 'Beverly Hills',
        state: 'CA',
        county: 'Los Angeles',
        lat: 34.0901,
        lon: -118.4065,
        timezone: 'America/Los_Angeles',
        createdAt: '2024-01-15T10:30:00Z',
        buzzSeededAt: null,
        courtsSeededAt: null,
      },
      {
        zip: '60601',
        city: 'Chicago',
        state: 'IL',
        county: 'Cook',
        lat: 41.8819,
        lon: -87.6278,
        timezone: 'America/Chicago',
        createdAt: '2024-01-15T10:30:00Z',
        buzzSeededAt: '2024-01-18T10:00:00Z',
        courtsSeededAt: null,
      },
      {
        zip: '77001',
        city: 'Houston',
        state: 'TX',
        county: 'Harris',
        lat: 29.7604,
        lon: -95.3698,
        timezone: 'America/Chicago',
        createdAt: '2024-01-15T10:30:00Z',
        buzzSeededAt: null,
        courtsSeededAt: '2024-01-19T11:00:00Z',
      },
      {
        zip: '33101',
        city: 'Miami',
        state: 'FL',
        county: 'Miami-Dade',
        lat: 25.7617,
        lon: -80.1918,
        timezone: 'America/New_York',
        createdAt: '2024-01-15T10:30:00Z',
        buzzSeededAt: null,
        courtsSeededAt: null,
      },
    ];
  }

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // New empty ZIP code
  const getNewZipCode = (): ZipMaster => ({
    zip: '',
    city: '',
    state: '',
    county: '',
    lat: 0,
    lon: 0,
    timezone: 'America/New_York',
    createdAt: new Date().toISOString(),
    buzzSeededAt: null,
    courtsSeededAt: null,
  });

  // Check if creating new vs editing existing
  const isCreating = editingZip && !zipCodes.find(z => z.zip === editingZip.zip);

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  // Sort data
  const sortedZipCodes = useMemo(() => {
    if (!sortConfig || !sortConfig.direction) {
      return zipCodes;
    }

    return [...zipCodes].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof ZipMaster];
      const bValue = b[sortConfig.key as keyof ZipMaster];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [zipCodes, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = '' }: { label: string; sortKey: string; className?: string }) => {
    const isActive = sortConfig?.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-200 hover:bg-slate-800/50 transition-colors ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1.5">
          <span>{label}</span>
          <span className="flex-shrink-0">
            {direction === 'asc' ? (
              <ChevronUp className="h-4 w-4 text-blue-400" />
            ) : direction === 'desc' ? (
              <ChevronDown className="h-4 w-4 text-blue-400" />
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5 text-slate-600" />
            )}
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <MapPin className="h-7 w-7 text-blue-400" />
            ZIP Master
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            View and manage ZIP code reference data
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadZipCodes}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
          <button
            onClick={() => setEditingZip(getNewZipCode())}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add ZIP Code
          </button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by ZIP code..."
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Page size dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Rows:</span>
          <div className="relative">
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              className="bg-slate-800 border border-slate-700 rounded-lg pl-3 pr-8 py-2 text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Environment badge */}
      {environment === 'prod' && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2 text-yellow-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Warning: You are viewing production data</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto rounded-lg border border-slate-800">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
          </div>
        ) : zipCodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <MapPin className="h-12 w-12 mb-3 opacity-50" />
            <p>No ZIP codes found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-800/50 sticky top-0">
              <tr>
                <SortableHeader label="ZIP" sortKey="zip" />
                <SortableHeader label="City" sortKey="city" />
                <SortableHeader label="State" sortKey="state" />
                <SortableHeader label="County" sortKey="county" />
                <SortableHeader label="Lat" sortKey="lat" />
                <SortableHeader label="Timezone" sortKey="timezone" />
                <SortableHeader label="Buzz Seeded" sortKey="buzzSeededAt" />
                <SortableHeader label="Courts Seeded" sortKey="courtsSeededAt" />
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sortedZipCodes.map((zip) => (
                <tr
                  key={zip.zip}
                  className="hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => setEditingZip({ ...zip })}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono font-medium text-white">{zip.zip}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{zip.city}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-slate-700 rounded text-xs font-medium text-slate-300">
                      {zip.state}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{zip.county || '-'}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                    {zip.lat?.toFixed(4)}, {zip.lon?.toFixed(4)}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{zip.timezone}</td>
                  <td className="px-4 py-3">
                    {zip.buzzSeededAt ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                        {formatDate(zip.buzzSeededAt)}
                      </span>
                    ) : (
                      <span className="text-slate-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {zip.courtsSeededAt ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                        {formatDate(zip.courtsSeededAt)}
                      </span>
                    ) : (
                      <span className="text-slate-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setEditingZip({ ...zip })}
                        className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteZipCode(zip.zip)}
                        className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
          <div className="text-sm text-slate-500">
            Showing {zipCodes.length} of {totalItems.toLocaleString()} ZIP codes
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingZip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-lg font-medium text-white">
                {isCreating ? 'Add ZIP Code' : `Edit ZIP Code: ${editingZip.zip}`}
              </h2>
              <button
                onClick={() => setEditingZip(null)}
                className="p-1 rounded hover:bg-slate-800 text-slate-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* ZIP Code (only editable when creating) */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">ZIP Code *</label>
                <input
                  type="text"
                  value={editingZip.zip}
                  onChange={(e) => setEditingZip({ ...editingZip, zip: e.target.value })}
                  disabled={!isCreating}
                  maxLength={10}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">City *</label>
                  <input
                    type="text"
                    value={editingZip.city}
                    onChange={(e) => setEditingZip({ ...editingZip, city: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">State *</label>
                  <input
                    type="text"
                    value={editingZip.state}
                    onChange={(e) => setEditingZip({ ...editingZip, state: e.target.value.toUpperCase() })}
                    maxLength={2}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase"
                  />
                </div>
              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">County</label>
                <input
                  type="text"
                  value={editingZip.county}
                  onChange={(e) => setEditingZip({ ...editingZip, county: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Latitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingZip.lat}
                    onChange={(e) => setEditingZip({ ...editingZip, lat: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Longitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingZip.lon}
                    onChange={(e) => setEditingZip({ ...editingZip, lon: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Timezone</label>
                <select
                  value={editingZip.timezone}
                  onChange={(e) => setEditingZip({ ...editingZip, timezone: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="America/New_York">America/New_York (EST/EDT)</option>
                  <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                  <option value="America/Denver">America/Denver (MST/MDT)</option>
                  <option value="America/Phoenix">America/Phoenix (MST)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                  <option value="America/Anchorage">America/Anchorage (AKST/AKDT)</option>
                  <option value="Pacific/Honolulu">Pacific/Honolulu (HST)</option>
                </select>
              </div>

              {/* Read-only fields */}
              {!isCreating && (
                <div className="pt-4 border-t border-slate-800">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Metadata (Read-only)</div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Created:</span>
                      <span className="ml-2 text-slate-300">{formatDate(editingZip.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Buzz Seeded:</span>
                      <span className="ml-2 text-slate-300">{formatDate(editingZip.buzzSeededAt)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Courts Seeded:</span>
                      <span className="ml-2 text-slate-300">{formatDate(editingZip.courtsSeededAt)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
              <div>
                {!isCreating && (
                  <button
                    onClick={() => {
                      deleteZipCode(editingZip.zip);
                      setEditingZip(null);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditingZip(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={isCreating ? createZipCode : saveZipCode}
                  disabled={isSubmitting || !editingZip.zip || !editingZip.city || !editingZip.state}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
